import React, { useEffect, useState } from 'react';
import adminApi from '../api/client';

interface Counts {
  date: string;
  timezone: string;
  total: number;
  with_isbn: number;
  without_isbn: number;
}

export function sriLankanToday(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: 'Asia/Colombo', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  return ['year', 'month', 'day'].map(key => parts.find(part => part.type === key)!.value).join('-');
}

export default function DailyBookCounts({ refreshKey = 0 }: { refreshKey?: number }) {
  const [date, setDate] = useState(() => sriLankanToday());
  const [attempt, setAttempt] = useState(0);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCounts(null); setError(null);
    if (!date) {
      setLoading(false); setError('Choose a date to view daily counts.');
      return;
    }
    setLoading(true);
    const load = async () => {
      try {
        const { data } = await adminApi.get<Counts>('/admin/books/daily-additions', { params: { date } });
        if (cancelled) return;
        if (!data || data.date !== date || data.timezone !== 'Asia/Colombo'
          || ![data.total, data.with_isbn, data.without_isbn].every(value => Number.isSafeInteger(value) && value >= 0)
          || data.total !== data.with_isbn + data.without_isbn) throw new Error('Invalid daily counts');
        setCounts(data);
      } catch (err: any) {
        if (cancelled) return;
        const status = err.response?.status;
        setError(status === 409
          ? 'Daily counts unavailable for this date. Catalogue dates or identities need review.'
          : status === 401 || status === 403
            ? 'An active admin session is required to view daily counts.'
            : 'Daily book counts unavailable. Please retry.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [date, attempt, refreshKey]);

  return (
    <section aria-label="Daily new books" aria-busy={loading}
      className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-800">Daily new books</h2>
          <p className="mt-1 text-xs text-gray-500">First saved books only. Personal library additions do not count.</p>
        </div>
        <div className="flex items-end gap-2">
          <label className="text-sm text-gray-700" htmlFor="daily-books-date">
            Date (Sri Lanka)
            <input id="daily-books-date" type="date" value={date} min="2000-01-01" max={sriLankanToday()}
              onChange={event => setDate(event.target.value)}
              className="mt-1 block rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20" />
          </label>
          <button type="button" onClick={() => setAttempt(value => value + 1)} disabled={loading || !date}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {error ? 'Retry daily counts' : 'Refresh daily counts'}
          </button>
        </div>
      </div>
      {loading ? <p role="status" className="mt-4 text-sm text-gray-500">Loading daily counts...</p>
        : error ? <p role="alert" className="mt-4 text-sm text-amber-800">{error}</p>
          : counts?.date === date && (
            <>
              <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {([['New books', counts.total], ['With ISBN', counts.with_isbn], ['Without ISBN', counts.without_isbn]] as const)
                  .map(([label, value]) => (
                    <div key={label} className="rounded-xl bg-gray-50 px-4 py-3">
                      <dt className="text-sm text-gray-600">{label}</dt>
                      <dd className="mt-1 text-2xl font-bold text-gray-800">{value.toLocaleString()}</dd>
                    </div>
                  ))}
              </dl>
              {counts.total === 0 && <p className="mt-3 text-sm text-gray-500">No new books on this date.</p>}
            </>
          )}
      <p className="mt-3 text-xs text-gray-500" title="Counts use retained catalogue records. Later ISBN repairs or duplicate corrections can change historical counts.">
        Sri Lanka time (UTC+05:30). ISBN counts use current saved values.
      </p>
    </section>
  );
}
